import React, { useState, useMemo } from 'react';
import {
  DollarSign,
  Globe,
  Search,
  Edit,
  Plus,
  Power,
  Layers,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useCurrenciesQuery } from '@/client/api/currency/currency.query';
import {
  useCreateCurrencyMutation,
  useUpdateCurrencyMutation,
  useDeactivateCurrencyMutation,
  useBulkAddCurrenciesMutation,
} from '@/client/api/currency/currency.mutation';
import type { Currency, CurrencyInput } from '@/client/types/currency';

const emptyForm: CurrencyInput = { name: '', code: '', country: '' };

const isActive = (c: Currency) => c.active !== false;

const CurrencyManagementPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'General Settings', href: '#' },
    { label: 'Currency Management' },
  ]);

  const { data: currencies = [], isLoading, isError } = useCurrenciesQuery();
  const createMutation = useCreateCurrencyMutation();
  const updateMutation = useUpdateCurrencyMutation();
  const deactivateMutation = useDeactivateCurrencyMutation();
  const bulkMutation = useBulkAddCurrenciesMutation();

  // Filters
  const [filters, setFilters] = useState({ searchTerm: '', status: '' });

  // Add/Edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CurrencyInput>(emptyForm);

  // Bulk modal
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<CurrencyInput[]>([{ ...emptyForm }]);

  const filteredCurrencies = useMemo(() => {
    const term = filters.searchTerm.trim().toLowerCase();
    return currencies.filter((currency) => {
      const matchesTerm =
        term === '' ||
        currency.name?.toLowerCase().includes(term) ||
        currency.code?.toLowerCase().includes(term) ||
        currency.country?.toLowerCase().includes(term);
      const matchesStatus =
        filters.status === '' ||
        (filters.status === 'active' ? isActive(currency) : !isActive(currency));
      return matchesTerm && matchesStatus;
    });
  }, [currencies, filters]);

  const handleAddCurrency = () => {
    setForm(emptyForm);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEditCurrency = (currency: Currency) => {
    setForm({ name: currency.name, code: currency.code, country: currency.country });
    setEditingId(currency.id);
    setIsModalOpen(true);
  };

  const handleSaveCurrency = async () => {
    const name = form.name.trim();
    const code = form.code.trim();
    const country = form.country.trim();
    if (!name || !code || !country) {
      toast.error('Name, code and country are required');
      return;
    }
    const payload: CurrencyInput = { name, code, country };
    try {
      if (editingId != null) {
        await updateMutation.mutateAsync({ id: editingId, ...payload });
        toast.success('Currency updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Currency created');
      }
      setIsModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save currency'));
    }
  };

  const handleDeactivate = async (currency: Currency) => {
    if (!window.confirm(`Deactivate ${currency.name} (${currency.code})?`)) return;
    try {
      await deactivateMutation.mutateAsync({ id: currency.id });
      toast.success('Currency deactivated');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to deactivate currency'));
    }
  };

  // Bulk helpers
  const updateBulkRow = (index: number, patch: Partial<CurrencyInput>) => {
    setBulkRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const addBulkRow = () => setBulkRows((rows) => [...rows, { ...emptyForm }]);
  const removeBulkRow = (index: number) =>
    setBulkRows((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== index) : rows));

  const openBulk = () => {
    setBulkRows([{ ...emptyForm }]);
    setIsBulkOpen(true);
  };

  const handleBulkSave = async () => {
    const rows = bulkRows
      .map((r) => ({ name: r.name.trim(), code: r.code.trim(), country: r.country.trim() }))
      .filter((r) => r.name || r.code || r.country);
    if (rows.length === 0) {
      toast.error('Add at least one currency');
      return;
    }
    const incomplete = rows.find((r) => !r.name || !r.code || !r.country);
    if (incomplete) {
      toast.error('Every row needs a name, code and country');
      return;
    }
    try {
      await bulkMutation.mutateAsync(rows);
      toast.success(`${rows.length} currenc${rows.length === 1 ? 'y' : 'ies'} added`);
      setIsBulkOpen(false);
      setBulkRows([{ ...emptyForm }]);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to bulk add currencies'));
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <DollarSign className="h-7 w-7 mr-3 text-green-600" />
              Currency Management
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage the currencies available across the platform
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={openBulk}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
              title="Bulk add currencies"
            >
              <Layers className="h-4 w-4 mr-2" />
              Bulk Add
            </button>
            <button
              onClick={handleAddCurrency}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Currency
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex space-x-4">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search by name, code or country..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            />
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Currency Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin" />
                    Loading currencies…
                  </td>
                </tr>
              )}
              {!isLoading && isError && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-red-600">
                    Failed to load currencies. Please try again.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && filteredCurrencies.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    No currencies found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !isError &&
                filteredCurrencies.map((currency) => {
                  const active = isActive(currency);
                  return (
                    <tr key={currency.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Globe className="h-5 w-5 mr-3 text-gray-400" />
                          <div className="text-sm font-medium text-gray-900">
                            {currency.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {currency.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {currency.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {active ? 'active' : 'inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditCurrency(currency)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                          {active && (
                            <button
                              onClick={() => handleDeactivate(currency)}
                              disabled={deactivateMutation.isPending}
                              className="text-red-600 hover:text-red-900 flex items-center disabled:opacity-50"
                            >
                              <Power className="h-4 w-4 mr-1" />
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Currency Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                {editingId != null ? 'Edit Currency' : 'Add New Currency'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. US Dollar"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Code
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value })}
                      placeholder="e.g. USD"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                      placeholder="e.g. United States of America"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCurrency}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center disabled:opacity-50"
                >
                  {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingId != null ? 'Update Currency' : 'Add Currency'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Add Modal */}
        {isBulkOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-16 mx-auto p-5 border w-[720px] shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Bulk Add Currencies</h3>

              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {bulkRows.map((row, index) => (
                  <div key={index} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center">
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={row.name}
                      onChange={(e) => updateBulkRow(index, { name: e.target.value })}
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={row.code}
                      onChange={(e) => updateBulkRow(index, { code: e.target.value })}
                      placeholder="Code"
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={row.country}
                      onChange={(e) => updateBulkRow(index, { country: e.target.value })}
                      placeholder="Country"
                    />
                    <button
                      onClick={() => removeBulkRow(index)}
                      disabled={bulkRows.length === 1}
                      className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30"
                      title="Remove row"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={addBulkRow}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add another row
              </button>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsBulkOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkSave}
                  disabled={bulkMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center disabled:opacity-50"
                >
                  {bulkMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Currencies
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyManagementPage;
